/*
 * MN Hero Slider — fade or slide, autoplay, dots, arrows, swipe, Theme Editor.
 */
class MnHeroSlider extends HTMLElement {
  constructor() {
    super();
    this.index = 0;
    this.slides = [];
    this.dots = [];
    this.track = null;
    this.viewport = null;
    this.prevButton = null;
    this.nextButton = null;
    this.autoplayTimer = null;
    this.abortController = null;
    this.reduceMotion = null;
    this.hoverMedia = null;
    this.isPointerInside = false;
    this.pointerId = null;
    this.dragStartX = 0;
    this.dragDeltaX = 0;
    this.isDragging = false;
    this.dragMoved = false;

    this.goTo = this.goTo.bind(this);
    this.next = this.next.bind(this);
    this.prev = this.prev.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleVisibility = this.handleVisibility.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.onDotClick = this.onDotClick.bind(this);
  }

  connectedCallback() {
    this.cleanup();
    this.abortController = new AbortController();
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.track = this.querySelector('[data-mn-hero-track]');
    this.viewport = this.querySelector('.mn-hero-slider__viewport');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.slides = [...this.querySelectorAll('[data-mn-hero-slide]')];
    this.dots = [...this.querySelectorAll('[data-mn-hero-dot]')];

    if (!(this.track instanceof HTMLElement) || this.slides.length === 0) return;

    const { signal } = this.abortController;

    this.prevButton?.addEventListener('click', this.prev, { signal });
    this.nextButton?.addEventListener('click', this.next, { signal });
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick, { signal }));
    this.addEventListener('keydown', this.handleKeydown, { signal });
    document.addEventListener('visibilitychange', this.handleVisibility, { signal });

    const dragTarget = this.viewport instanceof HTMLElement ? this.viewport : this;
    dragTarget.addEventListener('pointerdown', this.onPointerDown, { signal });
    dragTarget.addEventListener('pointermove', this.onPointerMove, { signal });
    dragTarget.addEventListener('pointerup', this.onPointerUp, { signal });
    dragTarget.addEventListener('pointercancel', this.onPointerUp, { signal });
    dragTarget.addEventListener('lostpointercapture', this.onPointerUp, { signal });
    dragTarget.addEventListener('click', this.onClickCapture, { capture: true, signal });

    this.addEventListener(
      'pointerenter',
      (event) => {
        if (!this.pauseOnHover || !this.canHoverPause || event.pointerType === 'touch') return;
        this.isPointerInside = true;
        this.stopAutoplay();
      },
      { signal }
    );
    this.addEventListener(
      'pointerleave',
      (event) => {
        if (event.pointerType === 'touch') return;
        this.isPointerInside = false;
        if (!this.isDragging) this.startAutoplay();
      },
      { signal }
    );
    this.addEventListener(
      'focusin',
      () => {
        this.stopAutoplay();
      },
      { signal }
    );
    this.addEventListener(
      'focusout',
      (event) => {
        if (this.contains(event.relatedTarget)) return;
        window.requestAnimationFrame(() => {
          if (!this.contains(document.activeElement)) this.startAutoplay();
        });
      },
      { signal }
    );

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.isSlideTransition) this.goTo(this.index, false);
      });
      this.resizeObserver.observe(this);
    }

    if (this.slides.length > 1) this.setAttribute('tabindex', '0');
    this.goTo(this.index, false);
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  get canHoverPause() {
    return this.hoverMedia?.matches === true;
  }

  get pauseOnHover() {
    return this.dataset.pauseHover !== 'false';
  }

  get autoplayEnabled() {
    return this.dataset.autoplay === 'true' && this.slides.length > 1;
  }

  get autoplaySpeed() {
    const speed = Number.parseInt(this.dataset.autoplaySpeed || '5000', 10);
    return Number.isFinite(speed) && speed >= 1000 ? speed : 5000;
  }

  get loopEnabled() {
    return this.dataset.loop !== 'false' && this.slides.length > 1;
  }

  get isSlideTransition() {
    return this.dataset.transition === 'slide';
  }

  get transitionMs() {
    if (this.reduceMotion?.matches) return 0;
    const duration = Number.parseInt(this.dataset.transitionMs || '700', 10);
    return Number.isFinite(duration) && duration >= 0 ? duration : 700;
  }

  onDotClick(event) {
    const index = Number.parseInt(event.currentTarget?.dataset.index || '0', 10);
    this.goTo(index);
    this.restartAutoplay();
  }

  syncSlideState() {
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('data-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.querySelectorAll('a, button').forEach((el) => {
        if (active) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    });

    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    if (this.prevButton instanceof HTMLButtonElement && this.nextButton instanceof HTMLButtonElement) {
      if (this.loopEnabled || this.slides.length <= 1) {
        this.prevButton.disabled = this.slides.length <= 1;
        this.nextButton.disabled = this.slides.length <= 1;
      } else {
        this.prevButton.disabled = this.index <= 0;
        this.nextButton.disabled = this.index >= this.slides.length - 1;
      }
    }
  }

  setTrackOffset(offset, animate = true) {
    if (!(this.track instanceof HTMLElement) || !this.isSlideTransition) return;
    this.track.style.transitionDuration = animate && this.transitionMs > 0 ? `${this.transitionMs}ms` : '0ms';
    this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
  }

  slideWidth() {
    return this.viewport?.getBoundingClientRect().width || this.getBoundingClientRect().width || 0;
  }

  goTo(index, animate = true) {
    if (this.slides.length === 0) return;

    const last = this.slides.length - 1;
    let nextIndex = index;
    if (this.loopEnabled) {
      if (nextIndex < 0) nextIndex = last;
      else if (nextIndex > last) nextIndex = 0;
    } else {
      nextIndex = Math.max(0, Math.min(index, last));
    }

    if (!animate) this.classList.add('is-jumping');

    this.index = nextIndex;
    this.syncSlideState();

    if (this.isSlideTransition) {
      this.setTrackOffset(this.slideWidth() * this.index, animate);
    }

    if (!animate) {
      window.requestAnimationFrame(() => {
        this.classList.remove('is-jumping');
      });
    }
  }

  next() {
    this.goTo(this.index + 1);
    this.restartAutoplay();
  }

  prev() {
    this.goTo(this.index - 1);
    this.restartAutoplay();
  }

  onPointerDown(event) {
    if (this.slides.length <= 1) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('button, a')) return;

    this.isDragging = true;
    this.dragMoved = false;
    this.pointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragDeltaX = 0;
    this.stopAutoplay();

    const target = this.viewport instanceof HTMLElement ? this.viewport : this;
    target.setPointerCapture?.(event.pointerId);
    this.classList.add('is-dragging');

    if (this.isSlideTransition) this.setTrackOffset(this.slideWidth() * this.index, false);
  }

  onPointerMove(event) {
    if (!this.isDragging || event.pointerId !== this.pointerId) return;

    this.dragDeltaX = event.clientX - this.dragStartX;
    if (Math.abs(this.dragDeltaX) > 6) this.dragMoved = true;

    if (!this.isSlideTransition) return;

    let offset = this.slideWidth() * this.index - this.dragDeltaX;
    if (!this.loopEnabled) {
      const max = this.slideWidth() * (this.slides.length - 1);
      offset = Math.max(0, Math.min(offset, max));
    }
    this.setTrackOffset(offset, false);
  }

  onPointerUp(event) {
    if (!this.isDragging) return;
    if (this.pointerId !== null && event.pointerId !== this.pointerId) return;

    const target = this.viewport instanceof HTMLElement ? this.viewport : this;
    if (this.pointerId !== null) {
      try {
        target.releasePointerCapture?.(this.pointerId);
      } catch (error) {
        // Capture may already be released.
      }
    }
    this.classList.remove('is-dragging');

    const width = this.slideWidth();
    const threshold = Math.min(80, width * 0.18);
    const delta = this.dragDeltaX;

    this.isDragging = false;
    this.pointerId = null;
    this.dragDeltaX = 0;

    if (event.pointerType === 'touch' || !this.canHoverPause) {
      this.isPointerInside = false;
    }

    if (Math.abs(delta) >= threshold) {
      if (delta < 0) this.next();
      else this.prev();
    } else {
      this.goTo(this.index, true);
      this.restartAutoplay();
    }
  }

  onClickCapture(event) {
    if (!this.dragMoved) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragMoved = false;
  }

  handleKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    }
  }

  handleVisibility() {
    if (document.hidden) this.stopAutoplay();
    else if (!this.isDragging) this.startAutoplay();
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplayEnabled || this.reduceMotion?.matches || document.hidden || this.isDragging) {
      return;
    }
    if (this.pauseOnHover && this.canHoverPause && this.isPointerInside) return;

    this.autoplayTimer = window.setInterval(() => {
      this.goTo(this.index + 1);
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer !== null) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    if (this.pauseOnHover && this.canHoverPause && this.isPointerInside) return;
    this.startAutoplay();
  }

  cleanup() {
    this.stopAutoplay();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.abortController?.abort();
    this.abortController = null;
    this.isDragging = false;
    this.isPointerInside = false;
    this.pointerId = null;
  }
}

if (!customElements.get('mn-hero-slider')) {
  customElements.define('mn-hero-slider', MnHeroSlider);
}

document.addEventListener('shopify:section:load', (event) => {
  const root = event.target;
  if (!(root instanceof Element)) return;
  root.querySelectorAll('mn-hero-slider').forEach((el) => {
    if (el instanceof MnHeroSlider && el.isConnected) el.connectedCallback();
  });
});

document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target?.closest?.('[data-mn-hero-slide]');
  const slider = event.target?.closest?.('mn-hero-slider');
  if (!(slide instanceof HTMLElement) || !(slider instanceof MnHeroSlider)) return;
  const slides = slider.slides.length
    ? slider.slides
    : [...slider.querySelectorAll('[data-mn-hero-slide]')];
  const index = slides.indexOf(slide);
  if (index >= 0) slider.goTo(index, false);
});
